<template>
  <MRow class="px-4">
    <MCol>
      <FlotoFormItem
        id="name"
        v-model="folder.name"
        :label="$t('name')"
        auto-focus
        rules="required|short_text"
        :placeholder="$t('name')"
      />
      <FlotoFormItem
        id="description"
        v-model="folder.description"
        :label="$t('description')"
        :rows="2"
        :placeholder="$t('description_instruction')"
        type="textarea"
      />
      <MRow v-if="enableMsp">
        <MCol :size="6">
          <FlotoFormItem id="company-picker" :label="$tc('company')">
            <CompanyPicker v-model="folder.companyIds" multiple />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <MRow v-if="folder.name !== 'Trash'">
        <MCol :sm="6" :size="12">
          <FlotoFormItem id="owner-picker" :label="$t('owner')">
            <FlotoRequesterPicker v-model="folder.ownerId" as-input />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <MRow>
        <MCol :sm="6" :size="12">
          <FlotoFormItem
            id="read-permission-support-portal"
            rules="required"
            :label="$t('read_permission_support_portal')"
          >
            <FlotoDropdownPicker
              v-model="folder.readPermission"
              as-input
              :options="readPermissionOptions"
              :disabled="disabled"
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <FlotoFormItem
        v-if="
          ['requester', 'requester_group'].indexOf(folder.readPermission) >= 0
        "
        id="requester-read-permission"
        rules="required"
        :label="$tc(folder.readPermission, 2)"
      >
        <component
          :is="
            folder.readPermission === 'requester'
              ? 'FlotoRequesterPicker'
              : 'FlotoRequesterGroupPicker'
          "
          v-model="folder.readUsers"
          mandatory-selection
          multiple
          as-input
          :disabled="disabled"
        />
      </FlotoFormItem>
      <MRow>
        <MCol :sm="6" :size="12">
          <FlotoFormItem
            id="read-permission-technician-portal"
            rules="required"
            :label="$t('read_permission_technician_portal')"
          >
            <FlotoDropdownPicker
              v-model="folder.techReadPermission"
              as-input
              :options="techReadPermissionOptions"
              :disabled="disabled"
              @change="resetWriteUsers"
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <FlotoFormItem
        v-if="
          ['technician', 'technician_group'].indexOf(
            folder.techReadPermission
          ) >= 0
        "
        id="technician-read-permission"
        rules="required"
        :label="$tc(folder.techReadPermission, 2)"
      >
        <component
          :is="
            folder.techReadPermission === 'technician'
              ? 'FlotoTechnicianPicker'
              : 'FlotoTechnicianGroupPicker'
          "
          v-model="folder.readPermissionTechnicianIds"
          multiple
          mandatory-selection
          as-input
          :disabled="disabled"
          @change="handleReadPermissionTechnicianIdsChange"
        />
      </FlotoFormItem>
      <MRow>
        <MCol :sm="6" :size="12">
          <FlotoFormItem
            id="write-permission"
            rules="required"
            :label="$t('write_permission')"
          >
            <FlotoDropdownPicker
              v-model="folder.writePermission"
              as-input
              :options="writePermissionOptions"
              :disabled="
                ['technician', 'technician_group'].indexOf(
                  folder.techReadPermission
                ) >= 0
                  ? true
                  : disabled
              "
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <FlotoFormItem
        v-if="
          ['technician', 'technician_group'].indexOf(folder.writePermission) >=
          0
        "
        id="technician-write-permission"
        vid="technician-write-permission"
        rules="required"
        :label="$tc(folder.writePermission, 2)"
      >
        <component
          :is="
            folder.writePermission === 'technician'
              ? 'FlotoTechnicianPicker'
              : 'FlotoTechnicianGroupPicker'
          "
          v-model="folder.writeUsers"
          multiple
          mandatory-selection
          as-input
          :disabled="disabled"
          :exclude-ids="
            folder.techReadPermission === 'technician'
              ? excludedTechnicianWriteUserIds
              : []
          "
          :hidden-options-keys="
            folder.techReadPermission === 'technician'
              ? excludedTechnicianWriteUserIds
              : []
          "
          :visible-options-keys="
            folder.techReadPermission === 'technician_group'
              ? visibleGroupWriteUserIds
              : []
          "
        />
      </FlotoFormItem>
    </MCol>
  </MRow>
</template>

<script>
import {
  readPermissions,
  writePermissions,
  techReadPermissions,
} from './helpers/utils'
import CompanyPicker from '@components/data-picker/company-picker'
import { MspConfigComputed } from '@state/modules/msp-config/helpers'
import { TechnicianComputed } from '@state/modules/technician'
import { UserGroupsComputed } from '@state/modules/user-group'

export default {
  name: 'FolderForm',
  components: { CompanyPicker },
  model: {
    event: 'change',
  },
  props: {
    folder: { type: Object, required: true },
    resetForm: { type: Function, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    this.readPermissionOptions = readPermissions().map((r) => r)
    this.writePermissionOptions = writePermissions().map((r) => r)
    this.techReadPermissionOptions = techReadPermissions().map((r) => r)
    return {
      excludedTechnicianWriteUserIds: [],
      visibleGroupWriteUserIds: [],
    }
  },
  computed: {
    ...MspConfigComputed,
    ...TechnicianComputed,
    ...UserGroupsComputed,
  },
  watch: {
    'folder.readPermission': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetForm({
          ...this.folder,
          readUsers: [],
        })
      }
    },
    'folder.writePermission': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetForm({
          ...this.folder,
          writeUsers:
            (this.folder.readPermissionTechnicianIds || []).length > 0
              ? this.folder.readPermissionTechnicianIds
              : [],
        })
      }
    },
    'folder.techReadPermission': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetForm({
          ...this.folder,
          writePermission: this.folder.techReadPermission,
        })
      }
    },
  },
  created() {
    if (this.folder.id) {
      this.resetForm({
        ...this.folder,
        writeUsers: this.folder.writeUsers,
      })
    } else {
      this.handleReadPermissionTechnicianIdsChange()
    }
  },
  methods: {
    handleReadPermissionTechnicianIdsChange() {
      this.resetWriteUsers()

      if (this.folder.techReadPermission === 'technician') {
        let options = []
        options = this.technicianOptions.filter(
          (i) =>
            (this.folder.readPermissionTechnicianIds || []).indexOf(i.id) === -1
        )
        this.excludedTechnicianWriteUserIds = options.map((i) => i.id)
      }

      if (this.folder.techReadPermission === 'technician_group') {
        let groupOptions = []
        groupOptions = this.technicianGroupsOptions.filter(
          (i) =>
            (this.folder.readPermissionTechnicianIds || []).indexOf(i.key) >= 0
        )
        this.visibleGroupWriteUserIds = groupOptions.map((i) => i.key)
      }
    },
    resetWriteUsers() {
      if (
        this.folder.techReadPermission !== 'all_technician' &&
        this.folder.techReadPermission === this.folder.writePermission
      ) {
        this.resetForm({
          ...this.folder,
          writeUsers: this.folder.readPermissionTechnicianIds,
        })
      }
    },
  },
}
</script>
